import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useActiveProduct } from "@/core/context/ActiveProductContext"
import { useLabel } from "@/core/context/LabelsContext"
import { useFormStore } from "@/core/form/store/FormStore"
import { MessageCommunityMembersDrawerContentMutation } from "@/organization/people/actions/message/__generated__/MessageCommunityMembersDrawerContentMutation.graphql"
import MessageCommunityMembersForm, {
  getEmailMessageInput,
  getSlackDirectMessageInput,
  MessageCommunityMembersFormState,
} from "@/organization/people/actions/message/MessageCommunityMembersForm"
import { OrganizationMembershipIdMemberChecklist } from "@/organization/people/member-checklist/CommunityMembersCheckListTitle"
import EditorUtils from "@components/editor/EditorUtils"
import { displaySuccessToast } from "@components/toast/ToastProvider"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { graphql } from "relay-runtime"

export type SendMessageToMembers =
  | OrganizationMembershipIdMemberChecklist[]
  | "community"
  | "product"

interface Props extends TestIDProps {
  onClose: () => void
  onMessage?: () => void
  sendTo: SendMessageToMembers
}

function MessageCommunityMembersDrawerContent({
  testid,
  onClose,
  onMessage,
  sendTo,
}: Props) {
  const memberLabel = useLabel("admin_member")
  const activeOrganization = useActiveOrganization()!
  const activeProduct = useActiveProduct()
  const organizationLabel = useLabel("organization")

  const isSendingToMemberships = Array.isArray(sendTo)

  const form = useFormStore<
    MessageCommunityMembersDrawerContentMutation,
    MessageCommunityMembersFormState
  >(
    graphql`
      mutation MessageCommunityMembersDrawerContentMutation($input: SendMessageInput!) {
        response: sendMessage(input: $input) {
          automation: node {
            id
          }
          errors {
            field
            message
          }
        }
      }
    `,
    {
      memberships: isSendingToMemberships ? sendTo : [],
      sendTo: isSendingToMemberships ? null : sendTo,
      emailMessageInput: {
        subject: "",
        richEditorBody: JSON.stringify(EditorUtils.createParagraphs([""])),
      },
      slackDirectMessageInput: {
        richEditorBody: JSON.stringify(EditorUtils.createParagraphs([""])),
        sendAs: null,
      },
      sendMessageMethod: "email",
    }
  )

  return (
    <MessageCommunityMembersForm
      testid={testid}
      form={form}
      onSubmit={handleSubmit}
      sendToName={
        sendTo === "product"
          ? activeProduct!.name
          : `your ${organizationLabel.singularLowercase}`
      }
    />
  )

  async function handleSubmit() {
    const { didSave } = await form.submit({
      organizationId: activeOrganization.id,
      organizationMembershipIds: form.state.sendTo
        ? null
        : form.state.memberships.map((m) => m.organizationMembershipId),
      sendTo: isSendingToMemberships ? null : sendTo,
      productId: form.state.sendTo === "product" ? activeProduct?.id : null,
      emailMessageInput: getEmailMessageInput(form),
      slackDirectMessageInput: getSlackDirectMessageInput(form),
    })
    if (!didSave) return

    const audience = form.state.sendTo
      ? `all ${memberLabel.plural}`
      : `${form.state.memberships.length} ${
          form.state.memberships.length === 1 ? memberLabel.singular : memberLabel.plural
        }`
    displaySuccessToast({
      message: `Sent message to ${audience}`,
      testid: `${testid}.success-toast`,
    })

    onClose()
    onMessage?.()
  }
}

export default observer(MessageCommunityMembersDrawerContent)
